import { CartErrorEvent } from '@shopify/events';
import { announceCartLinesUpdate } from '@theme/cart-events';

const GENERIC_ERROR = 'Sorry, we could not add that to your bag. Please try again.';
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ERROR_BUTTON_REENABLE_DELAY = 1000;

class CartAddError extends Error {}

/** @type {WeakSet<HTMLFormElement>} */
const pendingForms = new WeakSet();

/** @type {WeakMap<Element, ReturnType<typeof setTimeout>>} */
const errorTimeouts = new WeakMap();

/**
 * @typedef {object} BundleCart - Printed by snippets/bundle-cart-json.liquid.
 * @property {number} sourceProductId
 * @property {number} sourceVariantId
 * @property {number|null} sellingPlanId
 * @property {number} attachedProductId
 * @property {number} attachedVariantId
 * @property {string} attachedTitle
 */

/** @returns {string} */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
}

/**
 * Shows and announces the error, then clears both, as assets/product-form.js does.
 * @param {Element} scope - The product form component, or the form when there is none.
 * @param {string} message
 */
function showError(scope, message) {
  const error = scope.querySelector('.product-form-text__error');
  if (error instanceof HTMLElement) {
    for (const node of Array.from(error.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) node.remove();
    }
    error.append(message);
    error.classList.remove('hidden');
  }

  const liveRegion = scope.querySelector('[ref="liveRegion"]');
  if (liveRegion) liveRegion.textContent = message;

  clearTimeout(errorTimeouts.get(scope));
  errorTimeouts.set(
    scope,
    setTimeout(() => hideError(scope), ERROR_MESSAGE_DISPLAY_DURATION)
  );
}

/** @param {Element} scope */
function hideError(scope) {
  clearTimeout(errorTimeouts.get(scope));
  scope.querySelector('.product-form-text__error')?.classList.add('hidden');

  const liveRegion = scope.querySelector('[ref="liveRegion"]');
  if (liveRegion) liveRegion.textContent = '';
}

/**
 * Disables the scope's enabled add-to-cart buttons for the request. Returns only those, so a
 * sold-out button is never re-enabled.
 * @param {Element} scope
 * @returns {HTMLButtonElement[]}
 */
function disableButtons(scope) {
  const buttons = Array.from(scope.querySelectorAll('[ref="addToCartButton"]')).filter(
    (button) => button instanceof HTMLButtonElement && !button.disabled
  );
  for (const button of buttons) button.disabled = true;
  return /** @type {HTMLButtonElement[]} */ (buttons);
}

/**
 * @param {number} variantId - The bundle's source variant.
 * @returns {string} A token unique to one bundle add.
 */
export function newBundleToken(variantId) {
  return `${variantId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Same line shape as the legacy theme's postAddToCart: the cart pairs the two lines by token.
 * @param {BundleCart} bundle
 * @param {string} token
 * @param {number} quantity
 */
export function bundleLines(bundle, token, quantity) {
  /** @type {{id: number, quantity: number, properties: Record<string, string>, selling_plan?: number}} */
  const parent = {
    id: bundle.sourceVariantId,
    quantity,
    properties: {
      _attached_parent: 'true',
      _attached_token: token,
      _metafield_product_included: bundle.attachedTitle,
    },
  };
  if (bundle.sellingPlanId) parent.selling_plan = bundle.sellingPlanId;

  const child = {
    id: bundle.attachedVariantId,
    quantity,
    properties: {
      _attached_child: 'true',
      _attached_token: token,
      _isbinder: 'true',
    },
  };

  return { parent, child };
}

/**
 * Adds the bundle's two lines and announces them.
 * @param {BundleCart} bundle
 * @param {{sourceId?: string, productId?: string}} [source] - Where the add came from, for the announcement.
 */
export async function addBundleToCart(bundle, { sourceId, productId } = {}) {
  const { parent, child } = bundleLines(bundle, newBundleToken(bundle.sourceVariantId), 1);

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [parent, child], sections: cartSectionIds() }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data || data.status) throw new CartAddError(data?.description || data?.message || GENERIC_ERROR);

  await announceCartLinesUpdate({
    action: 'add',
    context: 'product',
    lines: [parent, child].map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
    source: 'bundle-add-to-cart',
    sourceId,
    sections: data.sections,
    items: data.items,
    variantId: String(bundle.sourceVariantId),
    productId,
  });
}

// Window capture runs ahead of Horizon's document-delegated `on:submit`, so the bundle's own variant is never posted.
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const data = form.querySelector('script[data-bundle-cart]');
    if (!data) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (pendingForms.has(form)) return;

    pendingForms.add(form);
    const scope = form.closest('product-form-component') ?? form;
    hideError(scope);
    const disabledButtons = disableButtons(scope);
    const enableButtons = () => {
      for (const button of disabledButtons) button.disabled = false;
    };

    const productForm = form.closest('product-form-component');
    Promise.resolve()
      .then(() =>
        addBundleToCart(JSON.parse(data.textContent || '{}'), {
          sourceId: productForm?.id,
          productId: productForm instanceof HTMLElement ? productForm.dataset.productId : undefined,
        })
      )
      .then(enableButtons)
      .catch((error) => {
        console.error('[bundle-add-to-cart]', error);
        const message = error instanceof CartAddError ? error.message : GENERIC_ERROR;
        showError(scope, message);
        scope.dispatchEvent(
          new CartErrorEvent({ error: message, code: error instanceof CartAddError ? 'INVALID' : 'SERVICE_UNAVAILABLE' })
        );
        setTimeout(enableButtons, ERROR_BUTTON_REENABLE_DELAY);
      })
      .finally(() => pendingForms.delete(form));
  },
  true
);
